/*
 * oath-predictive-search.js  -  Oath Jewelry  -  v1.25.10 (Nov 2026)
 *
 * Native search bar: Shopify Predictive Search API suggestions for the header search
 * bar and the results-page search box. Loaded with `defer` on every page, does no
 * work until a shopper opens the bar or types. Without JavaScript both forms still
 * submit to /search.
 */
(function () {
  'use strict';

  var MIN_CHARS = 2;
  var DEBOUNCE = 160;
  var cache = {};
  var uid = 0;

  function money(value) {
    var n = parseFloat(value);
    if (isNaN(n)) return '';
    var cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: cur }).format(n);
    } catch (e) {
      return '$' + n.toFixed(2);
    }
  }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function searchUrl(q) {
    return '/search?q=' + encodeURIComponent(q) + '&type=product,article&options%5Bunavailable_products%5D=last&options%5Bprefix%5D=last';
  }

  function fetchSuggest(q, signal) {
    var key = q.toLowerCase();
    if (cache[key]) return Promise.resolve(cache[key]);
    var url = '/search/suggest.json?q=' + encodeURIComponent(q) +
      '&resources[type]=query,collection,article,product' +
      '&resources[limit]=6&resources[limit_scope]=each' +
      '&resources[options][unavailable_products]=last';
    return fetch(url, { signal: signal, headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(function (d) { cache[key] = d.resources.results; return cache[key]; });
  }

  function attach(form) {
    var input = form.querySelector('input[name="q"]');
    if (!input) return;
    var id = 'oathSuggest' + (++uid);
    var panel = el('div', 'oath-suggest');
    panel.id = id;
    panel.hidden = true;
    panel.setAttribute('role', 'listbox');
    panel.setAttribute('aria-label', 'Search suggestions');
    var live = el('div', 'visually-hidden');
    live.setAttribute('role', 'status');
    form.appendChild(panel);
    form.appendChild(live);

    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-controls', id);
    input.setAttribute('autocomplete', 'off');

    var timer, ctrl, active = -1;

    function options() { return panel.querySelectorAll('[role="option"]'); }

    function close() {
      panel.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      active = -1;
    }

    function setActive(i) {
      var o = options();
      if (active > -1 && o[active]) { o[active].classList.remove('is-active'); o[active].setAttribute('aria-selected', 'false'); }
      active = i;
      if (i > -1 && o[i]) {
        o[i].classList.add('is-active');
        o[i].setAttribute('aria-selected', 'true');
        input.setAttribute('aria-activedescendant', o[i].id);
        o[i].scrollIntoView({ block: 'nearest' });
      } else {
        input.removeAttribute('aria-activedescendant');
      }
    }

    function fit() {
      var box = input.getBoundingClientRect();
      var room = window.innerHeight - box.bottom - 18;
      panel.style.maxHeight = Math.max(room, 220) + 'px';
    }

    function option(href, cls, n) {
      var a = el('a', cls);
      a.href = href;
      a.id = id + '-' + (n.i++);
      a.setAttribute('role', 'option');
      a.setAttribute('aria-selected', 'false');
      return a;
    }

    function group(title, items, make, n) {
      if (!items || !items.length) return null;
      var g = el('div', 'oath-suggest__group');
      g.appendChild(el('p', 'oath-suggest__heading', title));
      var ul = el('ul', 'oath-suggest__list');
      ul.setAttribute('role', 'presentation');
      items.forEach(function (it) {
        var li = el('li');
        li.setAttribute('role', 'presentation');
        li.appendChild(make(it, n));
        ul.appendChild(li);
      });
      g.appendChild(ul);
      return g;
    }

    function render(q, res) {
      panel.textContent = '';
      active = -1;
      var n = { i: 0 };
      var queries = (res.queries || []).filter(function (x) { return x.text && x.text.toLowerCase() !== q.toLowerCase(); }).slice(0, 4);
      var collections = (res.collections || []).slice(0, 4);
      var articles = (res.articles || []).slice(0, 2);
      var products = (res.products || []).slice(0, 6);
      var total = queries.length + collections.length + articles.length + products.length;

      if (!total) {
        panel.appendChild(el('p', 'oath-suggest__empty', 'No suggestions yet. Press Enter to search everything.'));
      } else {
        var grid = el('div', 'oath-suggest__grid');
        var side = el('div', 'oath-suggest__side');
        var main = el('div', 'oath-suggest__main');
        [
          group('Suggestions', queries, function (x, c) {
            return option(searchUrl(x.text), 'oath-suggest__link', c).appendChild(document.createTextNode(x.text)).parentNode;
          }, n),
          group('Categories', collections, function (x, c) {
            var a = option(x.url, 'oath-suggest__link', c);
            a.textContent = x.title;
            return a;
          }, n),
          group('Guides', articles, function (x, c) {
            var a = option(x.url, 'oath-suggest__link', c);
            a.textContent = x.title;
            return a;
          }, n)
        ].forEach(function (g) { if (g) side.appendChild(g); });

        if (products.length) {
          main.appendChild(el('p', 'oath-suggest__heading', 'Products'));
          var list = el('ul', 'oath-suggest__list oath-suggest__products');
          list.setAttribute('role', 'presentation');
          products.forEach(function (p) {
            var li = el('li');
            li.setAttribute('role', 'presentation');
            var a = option(p.url, 'oath-suggest__link oath-suggest__product', n);
            if (p.image) {
              var img = el('img', 'oath-suggest__thumb');
              img.src = p.image + (p.image.indexOf('?') > -1 ? '&' : '?') + 'width=112';
              img.alt = '';
              img.width = 56;
              img.height = 56;
              img.loading = 'lazy';
              a.appendChild(img);
            }
            var info = el('span', 'oath-suggest__info');
            info.appendChild(el('span', 'oath-suggest__title', p.title));
            var price = el('span', 'oath-suggest__price', money(p.price));
            if (p.available === false) price.appendChild(el('span', 'oath-suggest__soldout', 'Sold out'));
            info.appendChild(price);
            a.appendChild(info);
            li.appendChild(a);
            list.appendChild(li);
          });
          main.appendChild(list);
        }
        if (side.childNodes.length) grid.appendChild(side); else grid.style.gridTemplateColumns = 'minmax(0, 1fr)';
        grid.appendChild(main);
        panel.appendChild(grid);
      }

      var all = option(searchUrl(q), 'oath-suggest__all', n);
      all.textContent = 'See all results for “' + q + '”';
      panel.appendChild(all);

      panel.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      fit();
      live.textContent = total ? total + ' suggestions available' : 'No suggestions';
    }

    function run() {
      var q = input.value.trim();
      if (ctrl) ctrl.abort();
      if (q.length < MIN_CHARS) { close(); return; }
      ctrl = window.AbortController ? new AbortController() : null;
      fetchSuggest(q, ctrl ? ctrl.signal : undefined).then(function (res) {
        if (input.value.trim() === q) render(q, res);
      }).catch(function (err) {
        if (!err || err.name !== 'AbortError') close();
      });
    }

    input.addEventListener('input', function () {
      clearTimeout(timer);
      timer = setTimeout(run, DEBOUNCE);
    });

    input.addEventListener('focus', function () {
      if (input.value.trim().length >= MIN_CHARS && panel.hidden && panel.childNodes.length) {
        panel.hidden = false;
        input.setAttribute('aria-expanded', 'true');
        fit();
      }
    });

    input.addEventListener('keydown', function (e) {
      var o = options();
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (panel.hidden) { run(); return; }
        if (!o.length) return;
        e.preventDefault();
        var step = e.key === 'ArrowDown' ? 1 : -1;
        var next = active + step;
        if (next < -1) next = o.length - 1;
        if (next >= o.length) next = -1;
        setActive(next);
      } else if (e.key === 'Enter') {
        if (!panel.hidden && active > -1 && o[active]) {
          e.preventDefault();
          window.location.href = o[active].href;
        }
      } else if (e.key === 'Escape') {
        if (!panel.hidden) {
          e.stopPropagation();
          close();
        }
      }
    });

    /* Escape first closes the panel; the theme closes the bar on keyup, so hold that one back. */
    input.addEventListener('keyup', function (e) {
      if (e.key === 'Escape' && input.getAttribute('data-oath-esc') === '1') {
        input.removeAttribute('data-oath-esc');
        e.stopPropagation();
      }
    });
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !panel.hidden) input.setAttribute('data-oath-esc', '1');
    }, true);

    form.addEventListener('submit', function (e) {
      if (!input.value.trim()) { e.preventDefault(); input.focus(); }
    });

    document.addEventListener('click', function (e) {
      if (!form.contains(e.target)) close();
    });
    window.addEventListener('resize', function () { if (!panel.hidden) fit(); }, { passive: true });
  }

  /* Header search bar: place it directly under the header at any width, and focus the box. */
  function initDrawer() {
    var drawer = document.getElementById('SearchDrawer');
    var pageInput = document.getElementById('OathSearchInput');
    if (!drawer) return;

    function place() {
      var head = document.getElementById('shopify-section-header_new') || document.querySelector('.site-header');
      var bottom = head ? head.getBoundingClientRect().bottom : 0;
      drawer.style.setProperty('top', Math.max(Math.round(bottom), 0) + 'px', 'important');
    }

    /* Results page: the page already has a search box, so the icon goes there instead of opening a second one. */
    document.addEventListener('click', function (e) {
      var t = e.target.closest && e.target.closest('.js-drawer-open-top');
      if (!t) return;
      if (pageInput) {
        e.preventDefault();
        e.stopPropagation();
        window.scrollTo({ top: 0 });
        pageInput.focus();
        pageInput.select();
        return;
      }
      place();
      setTimeout(function () {
        place();
        var i = drawer.querySelector('input[name="q"]');
        if (i && document.documentElement.classList.contains('js-drawer-open-top')) i.focus();
      }, 60);
    }, true);

    window.addEventListener('resize', place, { passive: true });
    place();
  }

  function init() {
    var drawerForm = document.querySelector('#SearchDrawer form[role="search"]');
    if (drawerForm) attach(drawerForm);
    var pageForm = document.querySelector('[data-oath-page-search]');
    if (pageForm) attach(pageForm);
    initDrawer();
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
